import { useEffect, useMemo, useState } from "react";
import { Modal, OverlayTrigger, Popover } from "react-bootstrap";
import useThemeColor from "../../hooks/useThemeColor.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import "./manageMarketplaceCampaigns.css";

const defaultForm = {
  campaign_Name: "",
  campaign_Slug: "",
  headline: "",
  subheadline: "",
  campaign_Type: "PercentageOff",
  banner_Image: "",
  startDate: "",
  endDate: "",
  isActive: true,
  enrollment_Type: "Mixed",
  termsAndConditions: "T&Cs apply.",
  fromPriceAmount: "",
  targetCategory_ID: "",
  allowOptIn: true,
  autoApproveOptIn: false,
  city: "",
  category_ID: "",
  ruleMinDiscountPercent: "",
  requireActivePromotion: true,
  radiusKm: "5",
  restaurantCompIds: [],
};

const FIELD_HELP = {
  name: "Internal campaign name shown in SuperAdmin lists. Customers usually see the headline and banner instead.",
  slug: "URL-safe id used in marketplace links (e.g. /campaigns/your-slug). Keep it unique and lowercase with hyphens.",
  headline: "Short customer-facing title on the deal card / campaign page (e.g. “20% OFF on Your Order”).",
  subheadline: "Supporting line under the headline (e.g. “Your Favourite Food Just a Click Away!”).",
  campaignType:
    "Describes the marketing offer type for display. Percentage Off / From Price / Category Deal / Free Delivery — used for labels and filtering, not the enrollment logic.",
  enrollment:
    "Controls how restaurants can join this campaign. Manual = you assign them. Opt-in = restaurants request from BackOffice. Auto Rule = matched by city/category rules. Mixed = you can use a combination.",
  enrollmentManual:
    "Only restaurants you manually assign on the Restaurants tab are enrolled as Active. They do not appear as joinable deals in BackOffice unless Enrollment is Opt-in or Mixed.",
  enrollmentOptIn:
    "Restaurants can request to join from BackOffice → Go Fatafat → Campaigns. Requests become Pending (or Active if auto-approve is on). Approve them under Pending Opt-ins.",
  enrollmentAutoRule:
    "Restaurants that match the city / category rules are included automatically on the customer app — no enrollment row required. Opted-out restaurants stay excluded.",
  enrollmentMixed:
    "Combine manual assign + restaurant opt-in + auto rules. Typical for platform deals where some venues are forced in and others can join.",
  schedule: "Campaign is only visible to customers while Active and between start and end (server time).",
  targetCategory:
    "Optional filter when listing active campaigns for a cuisine category on the home screen. Leave empty to show for all categories.",
  fromPrice: "Optional “from price” amount for From Price style deals (display metadata).",
  terms: "Shown to customers on the campaign detail where T&Cs are displayed.",
  banner: "Image used on “Your daily deals” and campaign pages. Upload a file or paste an existing media path.",
  isActive: "Inactive campaigns never appear on Go Fatafat, even inside their date range.",
  allowOptIn:
    "When on (and Enrollment is Opt-in or Mixed), the campaign appears in BackOffice for restaurants to Join. When off, only SuperAdmin assignment / auto-rules apply.",
  autoApproveOptIn:
    "If on, restaurant join requests become Active immediately. If off, they stay Pending until you Approve or Reject on the Pending Opt-ins tab.",
  ruleCity:
    "Auto-match only restaurants whose company city equals this value. Leave blank to ignore city. Also used when listing campaigns for a customer city.",
  ruleCategory:
    "Auto-match only restaurants that have this Go Fatafat restaurant category assigned (company categories). Leave Any to ignore category.",
  ruleMinDiscount:
    "Hide restaurants whose current max % discount is below this value on Go Fatafat (and in BackOffice requirements). Example: 20 here and “Up to 10% off” on the restaurant → excluded. Saved once for both campaign display and rule filter.",
  requirePromo:
    "When on, restaurants must have at least one currently valid discount/promotion to appear in the campaign on Go Fatafat.",
  radiusKm:
    "Customer must be within this distance (km) of the restaurant for it to appear in the campaign. Uses delivery address coordinates.",
  customerFilters:
    "These filters apply for every enrollment mode. They control which restaurants customers see inside the deal (together with location).",
  manualRestaurants:
    "Select companies to enroll as Manual + Active when you save. These show in the campaign even without matching auto rules (still subject to customer filters on the app).",
  pendingOptIns:
    "Restaurants that requested join and are waiting for approval. Approve = Active in campaign. Reject = Rejected (they can request again later if the campaign stays joinable).",
};

const ENROLLMENT_OPTIONS = [
  {
    value: "Manual",
    title: "Manual",
    short: "You assign restaurants",
    helpKey: "enrollmentManual",
  },
  {
    value: "OptIn",
    title: "Opt-in",
    short: "Restaurants request join",
    helpKey: "enrollmentOptIn",
  },
  {
    value: "AutoRule",
    title: "Auto rule",
    short: "Match by city / category",
    helpKey: "enrollmentAutoRule",
  },
  {
    value: "Mixed",
    title: "Mixed",
    short: "Manual + opt-in + rules",
    helpKey: "enrollmentMixed",
  },
];

const FieldInfo = ({ helpKey, title }) => {
  const text = FIELD_HELP[helpKey];
  if (!text) return null;

  const popover = (
    <Popover id={`mc-help-${helpKey}`} className="mc-help-popover">
      <Popover.Header as="h6">{title || "How this works"}</Popover.Header>
      <Popover.Body>{text}</Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={["hover", "focus", "click"]}
      placement="top"
      overlay={popover}
      rootClose
    >
      <button
        type="button"
        className="mc-info-btn"
        aria-label={`About ${title || helpKey}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <i className="fa fa-info-circle" aria-hidden="true" />
      </button>
    </OverlayTrigger>
  );
};

const FieldLabel = ({ children, helpKey, htmlFor }) => (
  <label className="mc-label" htmlFor={htmlFor}>
    <span>{children}</span>
    {helpKey ? <FieldInfo helpKey={helpKey} title={children} /> : null}
  </label>
);

const SwitchRow = ({ name, checked, onChange, label, helpKey }) => (
  <div className="mc-switch-row">
    <label className="form-check form-switch m-0 mc-switch-label">
      <input
        className="form-check-input"
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="form-check-label ms-2">{label}</span>
    </label>
    {helpKey ? <FieldInfo helpKey={helpKey} title={label} /> : null}
  </div>
);

const rulesTabLabel = (enrollmentType) => {
  if (enrollmentType === "Manual") return "Restaurants";
  if (enrollmentType === "AutoRule" || enrollmentType === "OptIn") return "Rules";
  return "Rules & Restaurants";
};

const AddUpdateCampaignModal = ({
  show,
  onHide,
  onSubmit,
  isEdit,
  campaign,
  companies,
  categories,
  pendingOptIns,
  onApproveOptIn,
  onRejectOptIn,
}) => {
  const themeColor = useThemeColor();
  const [formData, setFormData] = useState(defaultForm);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const apiBaseUrl =
    process.env.REACT_APP_Environment === "development"
      ? process.env.REACT_APP_API_URL
      : process.env.REACT_APP_API_URL_Production;

  useEffect(() => {
    if (!show) return;
    setActiveTab("details");
    setRestaurantSearch("");
    if (isEdit && campaign) {
      const enrollmentType = campaign.enrollment_Type || campaign.enrollmentType || "Mixed";
      const ruleMin =
        campaign.rule?.minDiscountPercent ?? campaign.minDiscountPercent ?? "";
      setFormData({
        campaign_Name: campaign.campaign_Name || campaign.name || "",
        campaign_Slug: campaign.campaign_Slug || campaign.slug || "",
        headline: campaign.headline || "",
        subheadline: campaign.subheadline || "",
        campaign_Type: campaign.campaign_Type || campaign.campaignType || "PercentageOff",
        banner_Image: campaign.banner_Image || campaign.bannerImage || "",
        startDate: (campaign.startDate || "").toString().slice(0, 16),
        endDate: (campaign.endDate || "").toString().slice(0, 16),
        isActive: campaign.isActive ?? true,
        enrollment_Type: enrollmentType,
        termsAndConditions: campaign.termsAndConditions || "",
        fromPriceAmount: campaign.fromPriceAmount ?? "",
        targetCategory_ID: campaign.targetCategory_ID ?? campaign.targetCategoryId ?? "",
        allowOptIn:
          enrollmentType === "OptIn" || enrollmentType === "Mixed"
            ? campaign.allowOptIn ?? true
            : false,
        autoApproveOptIn: campaign.autoApproveOptIn ?? false,
        city: campaign.rule?.city || "",
        category_ID: campaign.rule?.category_ID ?? campaign.rule?.categoryId ?? "",
        ruleMinDiscountPercent: ruleMin,
        requireActivePromotion: campaign.rule?.requireActivePromotion ?? false,
        radiusKm: campaign.rule?.radiusKm ?? "5",
        restaurantCompIds: (campaign.restaurants || [])
          .filter((r) => {
            const status = (r.enrollment_Status || r.enrollmentStatus || "Active").toString();
            return status === "Active";
          })
          .map((r) => Number(r.compID || r.compId))
          .filter((id) => Number.isFinite(id) && id > 0),
      });
    } else {
      setFormData(defaultForm);
    }
  }, [isEdit, campaign, show]);

  const enrollmentType = formData.enrollment_Type;
  const showAutoMatch = enrollmentType === "AutoRule" || enrollmentType === "Mixed";
  const showManualRestaurants = enrollmentType === "Manual" || enrollmentType === "Mixed";
  const showOptInControls = enrollmentType === "OptIn" || enrollmentType === "Mixed";
  const showPendingTab = isEdit && showOptInControls;
  const showMinDiscount =
    formData.campaign_Type === "PercentageOff" || formData.campaign_Type === "CategoryDeal";
  const showFromPrice = formData.campaign_Type === "FromPrice";

  useEffect(() => {
    if (activeTab === "optins" && !showPendingTab) setActiveTab("details");
  }, [activeTab, showPendingTab]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const setEnrollmentType = (value) => {
    setFormData((prev) => ({
      ...prev,
      enrollment_Type: value,
      allowOptIn: value === "OptIn" || value === "Mixed",
      autoApproveOptIn: value === "OptIn" || value === "Mixed" ? prev.autoApproveOptIn : false,
    }));
  };

  const matchesRestaurantSearch = (name, city, id) => {
    const q = restaurantSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (name || "").toLowerCase().includes(q) ||
      (city || "").toLowerCase().includes(q) ||
      String(id || "").includes(q)
    );
  };

  const restaurantMetaById = useMemo(() => {
    const map = new Map();
    (companies || []).forEach((c) => {
      const id = Number(c.compID || c.compId);
      if (!Number.isFinite(id) || id <= 0) return;
      map.set(id, {
        id,
        name: c.compName || c.name || `Company ${id}`,
        city: c.compCity || "",
        source: null,
        status: null,
      });
    });
    (campaign?.restaurants || []).forEach((r) => {
      const id = Number(r.compID || r.compId);
      if (!Number.isFinite(id) || id <= 0) return;
      const existing = map.get(id);
      map.set(id, {
        id,
        name: r.compName || r.name || existing?.name || `Company ${id}`,
        city: r.compCity || existing?.city || "",
        source: r.enrollment_Source || r.enrollmentSource || existing?.source || null,
        status: r.enrollment_Status || r.enrollmentStatus || existing?.status || null,
      });
    });
    return map;
  }, [companies, campaign]);

  const selectedRestaurantSet = useMemo(
    () => new Set(formData.restaurantCompIds.map(Number)),
    [formData.restaurantCompIds]
  );

  const addedRestaurants = useMemo(() => {
    return formData.restaurantCompIds
      .map((compId) => {
        const id = Number(compId);
        return (
          restaurantMetaById.get(id) || {
            id,
            name: `Company ${id}`,
            city: "",
            source: "Manual",
            status: "Active",
          }
        );
      })
      .filter((r) => matchesRestaurantSearch(r.name, r.city, r.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [formData.restaurantCompIds, restaurantMetaById, restaurantSearch]);

  const availableRestaurants = useMemo(() => {
    return (companies || [])
      .map((c) => {
        const id = Number(c.compID || c.compId);
        const meta = restaurantMetaById.get(id);
        return {
          id,
          name: meta?.name || c.compName || c.name || `Company ${id}`,
          city: meta?.city || c.compCity || "",
          source: null,
          status: null,
        };
      })
      .filter(
        (r) =>
          Number.isFinite(r.id) &&
          r.id > 0 &&
          !selectedRestaurantSet.has(r.id) &&
          matchesRestaurantSearch(r.name, r.city, r.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, restaurantMetaById, selectedRestaurantSet, restaurantSearch]);

  const toggleRestaurant = (compId) => {
    const id = Number(compId);
    if (!Number.isFinite(id) || id <= 0) return;
    setFormData((prev) => {
      const exists = prev.restaurantCompIds.includes(id);
      return {
        ...prev,
        restaurantCompIds: exists
          ? prev.restaurantCompIds.filter((x) => x !== id)
          : [...prev.restaurantCompIds, id],
      };
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("ImageFile", file);
    try {
      setUploadingImage(true);
      const response = await axiosInstance.post(APIs.POST_UPLOAD_MARKETPLACE_CAMPAIGN_BANNER, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.status) {
        setFormData((prev) => ({ ...prev, banner_Image: response.data?.data?.filePath || "" }));
        showSuccessToast("Banner uploaded.");
      } else {
        showErrorToast(response.data?.message || "Upload failed.");
      }
    } catch {
      showErrorToast("Upload failed.");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleSubmit = () => {
    if (!formData.campaign_Name.trim() || !formData.campaign_Slug.trim()) {
      showErrorToast("Campaign name and slug are required.");
      setActiveTab("details");
      return;
    }

    const parsedMinDiscount =
      !showMinDiscount || formData.ruleMinDiscountPercent === ""
        ? null
        : parseFloat(formData.ruleMinDiscountPercent);

    onSubmit(
      {
        campaign_ID: campaign?.campaign_ID || campaign?.id || 0,
        campaign_Name: formData.campaign_Name.trim(),
        campaign_Slug: formData.campaign_Slug.trim(),
        headline: formData.headline.trim(),
        subheadline: formData.subheadline.trim(),
        campaign_Type: formData.campaign_Type,
        banner_Image: formData.banner_Image || null,
        // Order is managed by drag-and-drop on the list. Keep existing on edit; insert SP assigns next when 0.
        display_Order: isEdit
          ? campaign?.display_Order ?? campaign?.displayOrder ?? 0
          : 0,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: formData.isActive,
        enrollment_Type: formData.enrollment_Type,
        termsAndConditions: formData.termsAndConditions,
        // Dual-write: one UI field keeps campaign display + rule filter in sync
        minDiscountPercent: parsedMinDiscount,
        fromPriceAmount:
          !showFromPrice || formData.fromPriceAmount === ""
            ? null
            : parseFloat(formData.fromPriceAmount),
        targetCategory_ID:
          formData.targetCategory_ID === "" ? null : parseInt(formData.targetCategory_ID, 10),
        allowOptIn: showOptInControls ? formData.allowOptIn : false,
        autoApproveOptIn: showOptInControls ? formData.autoApproveOptIn : false,
        city: showAutoMatch ? formData.city.trim() || null : null,
        category_ID:
          showAutoMatch && formData.category_ID !== ""
            ? parseInt(formData.category_ID, 10)
            : null,
        ruleMinDiscountPercent: parsedMinDiscount,
        requireActivePromotion: formData.requireActivePromotion,
        radiusKm: formData.radiusKm === "" ? null : parseFloat(formData.radiusKm),
        restaurantCompIds: showManualRestaurants ? formData.restaurantCompIds : [],
      },
      isEdit
    );
  };

  const bannerSrc = formData.banner_Image
    ? formData.banner_Image.startsWith("http")
      ? formData.banner_Image
      : `${(apiBaseUrl || "").replace(/\/$/, "")}/${formData.banner_Image
          .replace(/\\/g, "/")
          .replace(/^\//, "")}`
    : "";

  const pendingCount = (pendingOptIns || []).length;
  const tabs = [
    { key: "details", label: "Details", icon: "fa-pen-to-square" },
    { key: "rules", label: rulesTabLabel(enrollmentType), icon: "fa-store" },
    ...(showPendingTab
      ? [{ key: "optins", label: `Pending Opt-ins (${pendingCount})`, icon: "fa-clock" }]
      : []),
  ];

  const enrollmentHelp =
    FIELD_HELP[
      ENROLLMENT_OPTIONS.find((o) => o.value === formData.enrollment_Type)?.helpKey || "enrollment"
    ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      backdrop="static"
      keyboard={false}
      className="mc-modal"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>
            {isEdit ? "Update Go Fatafat Campaign" : "Add Go Fatafat Campaign"}
          </Modal.Title>
          <p className="mc-modal-subtitle mb-0">
            Hover or tap the <i className="fa fa-info-circle" aria-hidden="true" /> icons for how each
            option affects Go Fatafat.
          </p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="mc-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mc-tab ${activeTab === tab.key ? "is-active" : ""}`}
              style={activeTab === tab.key ? { background: themeColor } : undefined}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`fa ${tab.icon} me-1`} aria-hidden="true" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "details" && (
          <>
            <div className="mc-section">
              <h3 className="mc-section-title">Identity</h3>
              <p className="mc-section-hint">
                Name and slug are for admin / URLs. Headline and banner are what customers see.
              </p>
              <div className="mc-form-grid">
                <div>
                  <FieldLabel helpKey="name">Name</FieldLabel>
                  <input
                    className="mc-input"
                    name="campaign_Name"
                    value={formData.campaign_Name}
                    onChange={handleChange}
                    placeholder="e.g. Weekend 20% Off"
                  />
                </div>
                <div>
                  <FieldLabel helpKey="slug">Slug</FieldLabel>
                  <input
                    className="mc-input"
                    name="campaign_Slug"
                    value={formData.campaign_Slug}
                    onChange={handleChange}
                    placeholder="weekend-20-off"
                  />
                </div>
                <div>
                  <FieldLabel helpKey="headline">Headline</FieldLabel>
                  <input
                    className="mc-input"
                    name="headline"
                    value={formData.headline}
                    onChange={handleChange}
                    placeholder="Up to 30% off"
                  />
                </div>
                <div>
                  <FieldLabel helpKey="subheadline">Subheadline</FieldLabel>
                  <input
                    className="mc-input"
                    name="subheadline"
                    value={formData.subheadline}
                    onChange={handleChange}
                    placeholder="Daily deals"
                  />
                </div>
              </div>
            </div>

            <div className="mc-section">
              <div className="mc-section-title-row">
                <h3 className="mc-section-title mb-0">How restaurants join</h3>
                <FieldInfo helpKey="enrollment" title="Enrollment" />
              </div>
              <p className="mc-section-hint">
                Choose one mode. The Rules / Restaurants tab only shows what that mode needs.
              </p>
              <div className="mc-enroll-grid">
                {ENROLLMENT_OPTIONS.map((opt) => {
                  const selected = formData.enrollment_Type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`mc-enroll-card ${selected ? "is-selected" : ""}`}
                      style={
                        selected
                          ? { borderColor: themeColor, boxShadow: `0 0 0 1px ${themeColor}` }
                          : undefined
                      }
                      onClick={() => setEnrollmentType(opt.value)}
                    >
                      <div className="mc-enroll-card-head">
                        <span className="mc-enroll-card-title">{opt.title}</span>
                        <FieldInfo helpKey={opt.helpKey} title={opt.title} />
                      </div>
                      <span className="mc-enroll-card-short">{opt.short}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mc-callout">
                <i className="fa fa-lightbulb me-2" aria-hidden="true" />
                <span>{enrollmentHelp}</span>
              </div>
            </div>

            <div className="mc-section">
              <h3 className="mc-section-title">Offer & schedule</h3>
              <div className="mc-form-grid is-3">
                <div>
                  <FieldLabel helpKey="campaignType">Type</FieldLabel>
                  <select
                    className="mc-select"
                    name="campaign_Type"
                    value={formData.campaign_Type}
                    onChange={handleChange}
                  >
                    <option value="PercentageOff">Percentage Off</option>
                    <option value="FromPrice">From Price</option>
                    <option value="CategoryDeal">Category Deal</option>
                    <option value="FreeDelivery">Free Delivery</option>
                  </select>
                </div>
                <div>
                  <FieldLabel helpKey="targetCategory">Target category</FieldLabel>
                  <select
                    className="mc-select"
                    name="targetCategory_ID"
                    value={formData.targetCategory_ID}
                    onChange={handleChange}
                  >
                    <option value="">None</option>
                    {(categories || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel helpKey="schedule">Start date</FieldLabel>
                  <input
                    className="mc-input"
                    type="datetime-local"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <FieldLabel helpKey="schedule">End date</FieldLabel>
                  <input
                    className="mc-input"
                    type="datetime-local"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                  />
                </div>
                {showFromPrice ? (
                  <div>
                    <FieldLabel helpKey="fromPrice">From price</FieldLabel>
                    <input
                      className="mc-input"
                      type="number"
                      name="fromPriceAmount"
                      value={formData.fromPriceAmount}
                      onChange={handleChange}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mc-section">
              <h3 className="mc-section-title">Terms & banner</h3>
              <div className="mc-form-grid">
                <div className="mc-span-2">
                  <FieldLabel helpKey="terms">T&amp;Cs</FieldLabel>
                  <textarea
                    className="mc-textarea"
                    name="termsAndConditions"
                    value={formData.termsAndConditions}
                    onChange={handleChange}
                    rows={2}
                  />
                </div>
                <div>
                  <FieldLabel helpKey="banner">Banner image path</FieldLabel>
                  <input
                    className="mc-input"
                    name="banner_Image"
                    value={formData.banner_Image}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <FieldLabel helpKey="banner">Upload banner</FieldLabel>
                  <input
                    className="mc-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </div>
                {bannerSrc ? (
                  <div className="mc-span-2">
                    <div className="mc-banner-preview">
                      <img src={bannerSrc} alt="Campaign banner preview" />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mc-section">
              <h3 className="mc-section-title">
                {showOptInControls ? "Status & opt-in" : "Status"}
              </h3>
              <div className="mc-switches mc-switches-stack">
                <SwitchRow
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  label="Active"
                  helpKey="isActive"
                />
                {showOptInControls ? (
                  <>
                    <SwitchRow
                      name="allowOptIn"
                      checked={formData.allowOptIn}
                      onChange={handleChange}
                      label="Allow restaurant opt-in"
                      helpKey="allowOptIn"
                    />
                    <SwitchRow
                      name="autoApproveOptIn"
                      checked={formData.autoApproveOptIn}
                      onChange={handleChange}
                      label="Auto-approve opt-in"
                      helpKey="autoApproveOptIn"
                    />
                  </>
                ) : null}
              </div>
            </div>
          </>
        )}

        {activeTab === "rules" && (
          <>
            <div className="mc-section">
              <div className="mc-section-title-row">
                <h3 className="mc-section-title mb-0">Customer filters</h3>
                <FieldInfo helpKey="customerFilters" title="Customer filters" />
              </div>
              <p className="mc-section-hint">
                Applied for every enrollment mode. Controls which restaurants appear inside the deal
                for customers (with location).
              </p>
              <div className="mc-form-grid is-3">
                {showMinDiscount ? (
                  <div>
                    <FieldLabel helpKey="ruleMinDiscount">Min discount %</FieldLabel>
                    <input
                      className="mc-input"
                      type="number"
                      name="ruleMinDiscountPercent"
                      value={formData.ruleMinDiscountPercent}
                      onChange={handleChange}
                      placeholder="e.g. 20"
                    />
                  </div>
                ) : null}
                <div>
                  <FieldLabel helpKey="radiusKm">Radius (km)</FieldLabel>
                  <input
                    className="mc-input"
                    type="number"
                    name="radiusKm"
                    value={formData.radiusKm}
                    onChange={handleChange}
                  />
                </div>
                <div className="d-flex align-items-end">
                  <SwitchRow
                    name="requireActivePromotion"
                    checked={formData.requireActivePromotion}
                    onChange={handleChange}
                    label="Require active promotion"
                    helpKey="requirePromo"
                  />
                </div>
              </div>
              {showMinDiscount ? (
                <div className="mc-callout is-warn">
                  <i className="fa fa-triangle-exclamation me-2" aria-hidden="true" />
                  <span>
                    If min discount is 20%, a restaurant showing “Up to 10% off” will not appear in
                    this campaign for customers — even if it is enrolled.
                  </span>
                </div>
              ) : null}
            </div>

            {showAutoMatch ? (
              <div className="mc-section">
                <div className="mc-section-title-row">
                  <h3 className="mc-section-title mb-0">Auto-match rules</h3>
                  <FieldInfo helpKey="enrollmentAutoRule" title="Auto rules" />
                </div>
                <p className="mc-section-hint">
                  Restaurants matching city / category are included automatically on Go Fatafat (unless
                  opted out). No manual enrollment row is required.
                </p>
                <div className="mc-form-grid is-3">
                  <div>
                    <FieldLabel helpKey="ruleCity">Rule city</FieldLabel>
                    <input
                      className="mc-input"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="e.g. Multan"
                    />
                  </div>
                  <div>
                    <FieldLabel helpKey="ruleCategory">Rule category</FieldLabel>
                    <select
                      className="mc-select"
                      name="category_ID"
                      value={formData.category_ID}
                      onChange={handleChange}
                    >
                      <option value="">Any</option>
                      {(categories || []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : null}

            {showManualRestaurants ? (
              <div className="mc-section">
                <div className="mc-section-title-row">
                  <h3 className="mc-section-title mb-0">Manual restaurants</h3>
                  <FieldInfo helpKey="manualRestaurants" title="Manual restaurants" />
                </div>
                <p className="mc-section-hint">
                  Added restaurants stay in the campaign as Active when you save. Use Available to add
                  more. Added: <strong>{formData.restaurantCompIds.length}</strong>
                  {" · "}
                  Showing available: <strong>{availableRestaurants.length}</strong>
                </p>
                <input
                  className="mc-input mb-2"
                  type="search"
                  value={restaurantSearch}
                  onChange={(e) => setRestaurantSearch(e.target.value)}
                  placeholder="Search by name, city, or company ID…"
                />

                <div className="mc-restaurant-panels">
                  <div className="mc-restaurant-panel">
                    <div className="mc-restaurant-panel-head is-added">
                      <span>Added to campaign</span>
                      <span className="mc-restaurant-count">{addedRestaurants.length}</span>
                    </div>
                    <div className="mc-restaurant-list">
                      {addedRestaurants.length === 0 ? (
                        <div className="mc-empty py-4">
                          {formData.restaurantCompIds.length === 0
                            ? "No restaurants added yet."
                            : "No added restaurants match your search."}
                        </div>
                      ) : (
                        addedRestaurants.map((r) => (
                          <label key={`added-${r.id}`} className="mc-restaurant-item is-checked">
                            <input
                              type="checkbox"
                              checked
                              onChange={() => toggleRestaurant(r.id)}
                            />
                            <span className="mc-restaurant-item-text">
                              <span className="mc-name">{r.name}</span>
                              <span className="mc-sub">
                                ID {r.id}
                                {r.city ? ` · ${r.city}` : ""}
                                {r.source ? ` · ${r.source}` : ""}
                              </span>
                            </span>
                            <span className="mc-restaurant-badge is-added">Added</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="mc-restaurant-panel">
                    <div className="mc-restaurant-panel-head is-available">
                      <span>Available to add</span>
                      <span className="mc-restaurant-count">{availableRestaurants.length}</span>
                    </div>
                    <div className="mc-restaurant-list">
                      {availableRestaurants.length === 0 ? (
                        <div className="mc-empty py-4">
                          {(companies || []).length === 0
                            ? "No marketplace restaurants loaded."
                            : "No available restaurants match your search."}
                        </div>
                      ) : (
                        availableRestaurants.map((r) => (
                          <label key={`avail-${r.id}`} className="mc-restaurant-item">
                            <input
                              type="checkbox"
                              checked={false}
                              onChange={() => toggleRestaurant(r.id)}
                            />
                            <span className="mc-restaurant-item-text">
                              <span className="mc-name">{r.name}</span>
                              <span className="mc-sub">
                                ID {r.id}
                                {r.city ? ` · ${r.city}` : ""}
                              </span>
                            </span>
                            <span className="mc-restaurant-badge is-available">Add</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : enrollmentType === "AutoRule" ? (
              <div className="mc-section">
                <div className="mc-callout">
                  <i className="fa fa-store me-2" aria-hidden="true" />
                  <span>
                    Restaurants matching city / category appear automatically — no manual list for
                    Auto rule campaigns.
                  </span>
                </div>
              </div>
            ) : enrollmentType === "OptIn" ? (
              <div className="mc-section">
                <div className="mc-callout">
                  <i className="fa fa-handshake me-2" aria-hidden="true" />
                  <span>
                    Restaurants join from BackOffice. Approve requests on the Pending Opt-ins tab —
                    no manual restaurant list for Opt-in campaigns.
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}

        {activeTab === "optins" && showPendingTab && (
          <div className="mc-section">
            <div className="mc-section-title-row">
              <h3 className="mc-section-title mb-0">Pending restaurant opt-ins</h3>
              <FieldInfo helpKey="pendingOptIns" title="Pending opt-ins" />
            </div>
            <p className="mc-section-hint">
              Approve to put the restaurant in this campaign (Active). Reject to decline the request.
            </p>
            <div className="table-responsive">
              <table className="mc-table">
                <thead>
                  <tr>
                    <th>Restaurant</th>
                    <th>City</th>
                    <th>Requested</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingOptIns || []).length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="mc-empty">No pending opt-ins for this campaign.</div>
                      </td>
                    </tr>
                  ) : (
                    pendingOptIns.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <span className="mc-name">{item.compName}</span>
                        </td>
                        <td>{item.compCity || "—"}</td>
                        <td>
                          {item.joinedDate ? new Date(item.joinedDate).toLocaleString() : "—"}
                        </td>
                        <td>
                          <div className="mc-optin-actions justify-content-end">
                            <button
                              type="button"
                              className="mc-btn-primary"
                              style={{ background: themeColor }}
                              onClick={() => onApproveOptIn(item.id)}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="mc-btn-ghost"
                              onClick={() => onRejectOptIn(item.id)}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="mc-footer-btn" onClick={onHide}>
          Cancel
        </button>
        <button
          type="button"
          className="mc-footer-btn is-primary"
          style={{ background: themeColor }}
          onClick={handleSubmit}
        >
          {isEdit ? "Update" : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUpdateCampaignModal;
